import { html, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { SlotController } from '../../controllers/slot-controller';
import { OutlineCard } from '../../base/outline-card/outline-card';
import '../ghs-icon/ghs-icon';
import { classMap } from 'lit/directives/class-map.js';

import componentStyles from './ghs-column-cta.css.lit';

/**
 * The Icon Card component.
 * @element ghs-column-cta
 * @slot icon - The icon slot.
 * @slot largIcon - The icon that renders as the background
 * @slot content - The primary slot to render the card content.
 * @slot cta - The cta slot.
 */
@customElement('ghs-column-cta')
export class GhsColumnCta extends OutlineCard {
  static styles = [componentStyles];

  slots = new SlotController(this, true);

  /**
   * Whether or not the component should be 100% height of its parent.
   * If TRUE, the Host will be `height: 100%`.
   */
  @property({ type: Boolean, attribute: 'height-grow', reflect: true })
  heightGrow = false;

  @state() hasIconSlot: boolean;
  @state() hasLargeIconSlot: boolean;
  @state() hasContentSlot: boolean;
  @state() hasCtaSlot: boolean;

  @state() isHovered: boolean;

  /**
   * An accessible solution to making a entire component clickable if there's a link.
   */
  linkifyCards = () => {
    if (!this.hasCtaSlot) {
      return;
    }

    const ctaButton = this.querySelector('ghs-button');

    const ctaUrl = ctaButton?.getAttribute('url');

    if (!ctaUrl) {
      return;
    }

    const ctaTarget = ctaButton?.getAttribute('button-target') ?? undefined;

    this.addEventListener('mouseenter', () => {
      this.isHovered = true;
    });

    this.addEventListener('mouseleave', () => {
      this.isHovered = false;
    });

    this.addEventListener('click', ev => {
      ev.preventDefault();
      ev.stopPropagation();
      window.open(ctaUrl, ctaTarget);
    });
  };

  firstUpdated(): void {
    super.firstUpdated();
    this.hasIconSlot = this.slots.test('icon');
    this.hasLargeIconSlot = this.slots.test('large-icon');
    this.hasContentSlot = this.slots.test('content');
    this.hasCtaSlot = this.slots.test('cta');
    this.linkifyCards();
  }

  render(): TemplateResult {
    const wrapperClasses = {
      'card-wrapper': true,
      'is-hovered': this.isHovered,
    };

    return html`
      <div class="${classMap(wrapperClasses)}">
        <div class="card">
          ${this.hasLargeIconSlot
            ? html`<div class="card-large-icon">
                <slot name="large-icon"></slot>
              </div> `
            : nothing}
          <div class="card-body-wrapper">
            ${this.hasIconSlot
              ? html`<div class="card-icon">
                  <slot name="icon"></slot>
                </div> `
              : nothing}
            ${this.hasCtaSlot
              ? html`
                  <div class="card-cta">
                    <slot name="cta"></slot>
                  </div>
                `
              : nothing}
            ${this.hasContentSlot
              ? html`
                  <div class="card-content">
                    <slot name="content"></slot>
                  </div>
                `
              : nothing}
          </div>
        </div>
      </div>
    `;
  }
}
